import { TFunction } from 'i18next';

export enum AiToolCategory {
  Search = 'search',
  SearchVersions = 'search_versions',
  Read = 'read',
  List = 'list',
  ListVersions = 'list_versions',
  Language = 'language',
  Analytics = 'analytics',
  View = 'view',
  Working = 'working',
}

const CATEGORY_LABEL_KEYS: Record<AiToolCategory, string> = {
  [AiToolCategory.Search]: 'ai-tool-searching',
  [AiToolCategory.SearchVersions]: 'ai-tool-searching-history',
  [AiToolCategory.Read]: 'ai-tool-reading',
  [AiToolCategory.List]: 'ai-tool-listing',
  [AiToolCategory.ListVersions]: 'ai-tool-listing-versions',
  [AiToolCategory.Language]: 'ai-tool-switching-language',
  [AiToolCategory.Analytics]: 'ai-tool-analytics',
  [AiToolCategory.View]: 'ai-tool-viewing-file',
  [AiToolCategory.Working]: 'ai-tool-working',
};

// Tool names are machine identifiers chosen by the BE worker, not part of the
// schema; map the known families to a category by keyword and fall back to a
// generic "working" category so an unrecognised tool still reads sensibly.
// Current names are entity-first (documentation_search, documentation_version_read,
// ai_conversation_language_set, …); older persisted messages carry pre-rename
// leaf names (search, read_documentation, …) or even older double-dash
// namespaced ones — matching by keyword rather than exact equality keeps all
// three readable without a migration.
// Exported separately from the label so steps can be grouped by category
// before translating (see groupToolStepsByCategory).
export function getAiToolCategory(
  toolName: string | null | undefined,
): AiToolCategory {
  const name = (toolName ?? '').toLowerCase();
  if (name.includes('search') && name.includes('version'))
    return AiToolCategory.SearchVersions;
  if (name.includes('list') && name.includes('version'))
    return AiToolCategory.ListVersions;
  if (name.includes('search')) return AiToolCategory.Search;
  if (name.includes('read')) return AiToolCategory.Read;
  if (name.includes('list')) return AiToolCategory.List;
  if (name.includes('language')) return AiToolCategory.Language;
  if (name.includes('analytics')) return AiToolCategory.Analytics;
  if (name.includes('view')) return AiToolCategory.View;
  return AiToolCategory.Working;
}

export default function getAiToolLabel(
  toolName: string | null | undefined,
  t: TFunction<'translation', undefined>,
): string {
  return t(CATEGORY_LABEL_KEYS[getAiToolCategory(toolName)]);
}
